import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Globe } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

/**
 * MultiLanguageInput - A component for entering text in multiple languages
 * 
 * @param {string} label - The label for the input field
 * @param {string} defaultValue - The default/English value
 * @param {object} translations - Object with translations { fr: "...", es: "...", de: "..." }
 * @param {function} onDefaultChange - Callback when default value changes
 * @param {function} onTranslationsChange - Callback when translations change
 * @param {boolean} multiline - Whether to use textarea instead of input
 * @param {string} placeholder - Placeholder text
 */
const MultiLanguageInput = ({
  label,
  defaultValue = '',
  translations = {},
  onDefaultChange,
  onTranslationsChange,
  multiline = false,
  placeholder = '',
  required = false,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('en');

  const handleTranslationChange = (langCode, value) => {
    if (langCode === 'en') {
      onDefaultChange(value);
    } else {
      onTranslationsChange({
        ...translations,
        [langCode]: value,
      });
    }
  };

  const getValue = (langCode) => {
    if (langCode === 'en') return defaultValue;
    return translations[langCode] || '';
  };

  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="flex items-center gap-1">
          <Globe className="h-3 w-3 text-slate-400" />
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 h-8">
          {LANGUAGES.map((lang) => (
            <TabsTrigger
              key={lang.code}
              value={lang.code}
              className="text-xs px-2 py-1 data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <span className="mr-1">{lang.flag}</span>
              {lang.code.toUpperCase()}
            </TabsTrigger>
          ))}
        </TabsList>
        
        {LANGUAGES.map((lang) => (
          <TabsContent key={lang.code} value={lang.code} className="mt-2">
            <InputComponent
              value={getValue(lang.code)}
              onChange={(e) => handleTranslationChange(lang.code, e.target.value)}
              placeholder={`${placeholder} (${lang.name})${lang.code === 'en' ? ' - Required' : ' - Optional'}`}
              className={multiline ? 'min-h-[100px]' : ''}
              required={lang.code === 'en' && required}
            />
            {lang.code !== 'en' && !getValue(lang.code) && getValue('en') && (
              <p className="text-xs text-slate-400 mt-1">
                Will display English version if not translated
              </p>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

/**
 * Helper function to get translated content based on current language
 * @param {string} defaultValue - The default/English value
 * @param {object} translations - Object with translations
 * @param {string} currentLang - Current language code
 * @returns {string} The appropriate translation or default value
 */
export const getTranslatedContent = (defaultValue, translations, currentLang) => {
  if (!translations || currentLang === 'en') {
    return defaultValue || '';
  }
  return translations[currentLang] || defaultValue || '';
};

export default MultiLanguageInput;
